import React from 'react';

import CustomizedTable from 'components/common/components/tablev2/CustomizedTable';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { hasObjectKeys, parseDate } from 'utils/main';
import { setSelectedZones } from 'store/actions/dashboard/selectedZones';
import { setDashboardSelectedUavos } from 'store/actions/dashboard/selectedUavos';
import { setSelectedApprovedAreas } from 'store/actions/map/selectedApprovedAreas';
import { setDashboardSelectedMissionsApprovals } from 'store/actions/dashboard/selectedMissionsApprovals';
import { setDashboardItemSelected } from 'store/actions/dashboard/setDashboardItemSelected';
import { authPatchRequestWithParam } from 'helpers/apiRequests';
import { clearMapLayer, createLayer } from 'utils/map/layers';
import { addLayerToMap, zoomToGeojsonGeometryWithBuffer } from 'utils/map/map';
import mainStyles from 'consts/styles/RightScreenPart';
import { userStyles } from 'consts/styles/User';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Icons from 'consts/icons';
import { addMissionApprovals } from './utils/main';
import DisplayMissionsApprovalDetails from './DisplayDetails';

const MissionsApprovals = props => {
  const { isDashboardItemSelected, currentMissionsApprovals } = props;

  const timezone = useSelector(state => state.timezone);
  const map = useSelector(state => state.map);
  const subtitles = useSelector(state => state.language).close_;

  const [selectedMission, setSelectedMission] = React.useState(null);
  const [confirmationModalCase, setConfirmationModalCase] = React.useState(
    null
  );
  const layer = React.useRef(null);

  const componentClasses = mainStyles();
  const classesUser = userStyles();

  const dispatch = useDispatch();

  React.useEffect(() => {
    layer.current = createLayer('missionApprovals');
  }, []);

  const initMap = () => {
    if (!hasObjectKeys(map)) return;
    addLayerToMap(map, layer.current);
  };

  React.useEffect(initMap, [map]);

  React.useEffect(() => {
    if (selectedMission) {
      dispatch(setDashboardItemSelected(true));
      clearMapLayer(layer.current);
      addMissionApprovals(layer.current, [selectedMission]);
      handleZoomAfterSelect();
    } else {
      dispatch(setDashboardItemSelected(false));
      clearMapLayer(layer.current);
      setConfirmationModalCase(null);
    }
  }, [selectedMission]);

  const rowClicked = data => {
    setSelectedMission(
      currentMissionsApprovals.find(mission => mission.uid === data.id)
    );
    dispatch(setDashboardItemSelected(true));
    dispatch(setSelectedZones([]));
    dispatch(setDashboardSelectedUavos([]));
    dispatch(setSelectedApprovedAreas([]));
  };

  const updateApproval = async (approve = null, message = null) => {
    if (!approve || confirmationModalCase === null) {
      setConfirmationModalCase(null);
      return;
    }
    const response = await authPatchRequestWithParam(
      'missionApproval',
      selectedMission.uid,
      {
        status: confirmationModalCase ? 'accepted' : 'rejected',
        message,
      }
    );
    if (response?.status === 200) {
      const filteredMissions = currentMissionsApprovals.filter(
        approval => approval.uid !== response.data.uid
      );
      dispatch(setDashboardSelectedMissionsApprovals(filteredMissions));
      setSelectedMission(null);
      clearMapLayer(layer.current);
    }
  };

  const handleZoomAfterSelect = () => {
    zoomToGeojsonGeometryWithBuffer(map, selectedMission.mission.legs, 200000);
  };

  const handleZoom = () => {
    zoomToGeojsonGeometryWithBuffer(map, selectedMission.mission.legs);
  };

  React.useEffect(() => {
    if (!isDashboardItemSelected) setSelectedMission(null);
  }, [isDashboardItemSelected]);

  return (
    <>
      {!selectedMission ? (
        <div className={`${componentClasses.Wrapper}__switcher-wrapper`}>
          <CustomizedTable
            headers={['Uid', 'Start', 'Stop']}
            data={currentMissionsApprovals.map(approval => ({
              id: approval.uid,
              start: parseDate(approval.mission.start, timezone),
              stop: parseDate(approval.mission.stop, timezone),
            }))}
            onClickFunction={rowClicked}
            selectedRow={selectedMission}
            dashboard
          />
        </div>
      ) : (
        <Grid
          container
          className={`${classesUser.User}__dashboard--inline`}
        >
          <Tooltip title={subtitles} placement="bottom">
            <div
              className={`${classesUser.User}__settings__close`}
              onClick={() => setSelectedMission(null)}
              onKeyPress={() => setSelectedMission(null)}
              role="button"
              tabIndex={0}
            >
              <div
                className={`${classesUser.User}__settings__close__container`}
              >
                <Icons.Actions.Close />
              </div>
            </div>
          </Tooltip>
          <DisplayMissionsApprovalDetails
            selectedMission={selectedMission}
            setSelectedMission={setSelectedMission}
            confirmationModalCase={confirmationModalCase}
            setConfirmationModalCase={setConfirmationModalCase}
            handleZoom={handleZoom}
            confirmAction={updateApproval}
          />
        </Grid>
      )}
    </>
  );
};

MissionsApprovals.propTypes = {
  isDashboardItemSelected: PropTypes.bool,
  currentMissionsApprovals: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
  isDashboardItemSelected: state.isDashboardItemSelected,
});

export default connect(mapStateToProps)(React.memo(MissionsApprovals));
